import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star,
  ChevronLeft,
  ChevronRight,
  Check,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useFeedback } from "@/providers/FeedbackProvider";
import {
  FeedbackCategory,
  SubmitFeedbackPayload,
} from "@shared/types/FeedbackDto";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

type FeedbackStep = "quick" | "ratings" | "details" | "confirmation";

const QUICK_OPTIONS = [
  {
    id: "great",
    label: "Everything's great!",
    emoji: "👍",
    nps: 10,
    satisfaction: 5,
  },
  { id: "good", label: "Pretty good", emoji: "👌", nps: 8, satisfaction: 4 },
  {
    id: "better",
    label: "Could be better",
    emoji: "🤔",
    nps: null,
    satisfaction: null,
  },
];

const CATEGORIES: { value: FeedbackCategory; label: string }[] = [
  { value: "OVERALL", label: "Overall Experience" },
  { value: "COMMUNICATION", label: "Communication" },
  { value: "QUALITY", label: "Quality of Work" },
  { value: "TIMELINE", label: "Timeline & Delivery" },
  { value: "SUPPORT", label: "Support" },
  { value: "PROCESS", label: "Process" },
];

interface FeedbackModalProps {
  projectId?: string;
}

export function FeedbackModal({ projectId }: FeedbackModalProps) {
  const { isModalOpen, closeFeedbackModal, submitFeedback, modalInitialType } =
    useFeedback();
  const [step, setStep] = useState<FeedbackStep>("quick");
  const [npsScore, setNpsScore] = useState<number | null>(null);
  const [satisfactionRating, setSatisfactionRating] = useState<number | null>(
    null,
  );
  const [category, setCategory] = useState<FeedbackCategory>("OVERALL");
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isModalOpen) {
      setStep("quick");
      setNpsScore(null);
      setSatisfactionRating(null);
      setCategory(modalInitialType === "suggestion" ? "PROCESS" : "OVERALL");
      setComment("");
    }
  }, [isModalOpen, modalInitialType]);

  const handleQuickOption = async (option: (typeof QUICK_OPTIONS)[number]) => {
    if (option.nps === null) {
      setStep("ratings");
    } else {
      setNpsScore(option.nps);
      setSatisfactionRating(option.satisfaction);
      await handleSubmit(option.nps, option.satisfaction!, "");
    }
  };

  const handleSubmit = async (
    nps: number = npsScore!,
    satisfaction: number = satisfactionRating!,
    commentText: string = comment,
  ) => {
    setIsSubmitting(true);
    try {
      const payload: SubmitFeedbackPayload = {
        type: projectId ? "PROJECT_COMPLETION" : "GENERAL",
        category,
        npsScore: nps,
        satisfactionRating: satisfaction,
        comment: commentText,
        projectId,
        triggeredBy: "FLOATING_BUTTON",
      };
      await submitFeedback(payload);
      setStep("confirmation");
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedFromRatings =
    npsScore !== null && satisfactionRating !== null;

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={(open) => !open && closeFeedbackModal()}
    >
      <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden bg-background border-border">
        <DialogHeader className="p-6 pb-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-semibold">
              {step === "confirmation" ? "Thank You!" : "Share Your Feedback"}
            </DialogTitle>
          </div>
          {step !== "confirmation" && (
            <div className="flex gap-1.5 mt-4">
              {(["quick", "ratings", "details"] as FeedbackStep[]).map(
                (s, i) => (
                  <div
                    key={s}
                    className={cn(
                      "h-1 flex-1 rounded-full transition-colors",
                      step === s
                        ? "bg-primary"
                        : ["quick", "ratings", "details"].indexOf(step) > i
                          ? "bg-primary/50"
                          : "bg-muted",
                    )}
                  />
                ),
              )}
            </div>
          )}
        </DialogHeader>

        <div className="p-6">
          <AnimatePresence mode="wait">
            {step === "quick" && (
              <motion.div
                key="quick"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <p className="text-muted-foreground">
                  How would you describe your experience?
                </p>
                <div className="grid gap-3">
                  {QUICK_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleQuickOption(option)}
                      className="flex items-center gap-4 p-4 rounded-xl border border-border hover:border-primary hover:bg-primary/5 transition-all text-left group"
                    >
                      <span className="text-2xl">{option.emoji}</span>
                      <span className="font-medium group-hover:text-primary transition-colors">
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setStep("ratings")}
                  className="w-full text-center text-sm text-muted-foreground hover:text-primary transition-colors py-2"
                >
                  Give detailed feedback instead →
                </button>
              </motion.div>
            )}

            {step === "ratings" && (
              <motion.div
                key="ratings"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <Label className="text-sm font-medium mb-3 block">
                    How likely are you to recommend us? (0-10)
                  </Label>
                  <div className="flex gap-1">
                    {Array.from({ length: 11 }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => setNpsScore(i)}
                        className={cn(
                          "flex-1 aspect-square rounded-lg text-sm font-medium transition-all",
                          npsScore === i
                            ? "ring-2 ring-offset-2 ring-offset-background"
                            : "hover:scale-105",
                          i <= 6
                            ? cn(
                                "bg-red-500/20 text-red-500",
                                npsScore === i &&
                                  "ring-red-500 bg-red-500 text-white",
                              )
                            : i <= 8
                              ? cn(
                                  "bg-amber-500/20 text-amber-500",
                                  npsScore === i &&
                                    "ring-amber-500 bg-amber-500 text-white",
                                )
                              : cn(
                                  "bg-green-500/20 text-green-500",
                                  npsScore === i &&
                                    "ring-green-500 bg-green-500 text-white",
                                ),
                        )}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                    <span>Not likely</span>
                    <span>Very likely</span>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-3 block">
                    Overall satisfaction
                  </Label>
                  <div className="flex gap-2 justify-center">
                    {Array.from({ length: 5 }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => setSatisfactionRating(i + 1)}
                        className="p-2 transition-transform hover:scale-110"
                      >
                        <Star
                          className={cn(
                            "w-8 h-8 transition-colors",
                            satisfactionRating !== null &&
                              i < satisfactionRating
                              ? "fill-amber-400 text-amber-400"
                              : "text-muted-foreground/30",
                          )}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep("quick")}
                    className="flex-1"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep("details")}
                    disabled={!canProceedFromRatings}
                    className="flex-1"
                  >
                    Continue
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}

            {step === "details" && (
              <motion.div
                key="details"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Category
                  </Label>
                  <Select
                    value={category}
                    onValueChange={(v) => setCategory(v as FeedbackCategory)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Additional comments{" "}
                    <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <Textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value.slice(0, 1000))}
                    placeholder="What went well? What could we improve?"
                    rows={4}
                    className="resize-none"
                  />
                  <p className="text-xs text-muted-foreground mt-1 text-right">
                    {comment.length}/1000
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setStep("ratings")}
                    className="flex-1"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => handleSubmit()}
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Feedback"}
                    <Check className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}

            {step === "confirmation" && (
              <motion.div
                key="confirmation"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-6"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.1 }}
                  className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4"
                >
                  <Sparkles className="w-8 h-8 text-green-500" />
                </motion.div>
                <h3 className="text-xl font-semibold mb-2">
                  Thanks for your feedback!
                </h3>
                <p className="text-muted-foreground mb-6">
                  Your input helps us improve our services.
                </p>
                <div className="space-y-3">
                  <Button onClick={closeFeedbackModal} className="w-full">
                    Close
                  </Button>
                  <button
                    onClick={() => {
                      closeFeedbackModal();
                      window.location.href = "/feedback";
                    }}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    View your feedback history →
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
